'use client';

import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export interface ComboboxOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface ComboboxProps {
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  options: ComboboxOption[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  /**
   * Allow selecting a custom value by typing and choosing "Use …".
   * Useful for freeform categories, tags, etc.
   */
  allowCustom?: boolean;
  customValueLabel?: (value: string) => string;
  className?: string;
  leadingIcon?: React.ReactNode;
  leadingIconClassName?: string;
  labelClassName?: string;
  chevronClassName?: string;
  checkPosition?: 'left' | 'right';
  disabled?: boolean;
}

export function Combobox({
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  options,
  value,
  onValueChange,
  placeholder = 'Select option...',
  searchPlaceholder = 'Search...',
  emptyText = 'No results found.',
  allowCustom = false,
  customValueLabel,
  className,
  leadingIcon,
  leadingIconClassName,
  labelClassName,
  chevronClassName,
  checkPosition = 'left',
  disabled,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const selectedOption = options.find((option) => option.value === value);
  const selectedIcon = selectedOption?.icon ?? leadingIcon;
  const hasExactMatch = React.useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return false;
    return options.some(
      (opt) =>
        opt.value.toLowerCase() === normalized ||
        opt.label.toLowerCase() === normalized
    );
  }, [options, query]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          className={cn(
            'h-9 w-full justify-between overflow-hidden',
            !selectedOption && 'text-muted-foreground',
            className
          )}
          disabled={disabled}
        >
          {selectedIcon ? (
            <span
              data-slot="combobox-leading-icon"
              className={cn(
                'hidden shrink-0 items-center',
                leadingIconClassName
              )}
            >
              {selectedIcon}
            </span>
          ) : null}
          <span
            data-slot="combobox-label"
            className={cn('min-w-0 flex-1 truncate text-left', labelClassName)}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronsUpDown
            data-slot="combobox-chevron"
            className={cn('ml-2 h-4 w-4 shrink-0 opacity-50', chevronClassName)}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
      >
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {allowCustom && query.trim() && !hasExactMatch ? (
                <CommandItem
                  key={`custom-${query}`}
                  value={query}
                  onSelect={() => {
                    onValueChange(query.trim());
                    setOpen(false);
                    setQuery('');
                  }}
                >
                  <Check className={cn('mr-2 h-4 w-4 opacity-0')} />
                  {customValueLabel
                    ? customValueLabel(query.trim())
                    : `Use "${query.trim()}"`}
                </CommandItem>
              ) : null}
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => {
                    onValueChange(option.value === value ? '' : option.value);
                    setOpen(false);
                    setQuery('');
                  }}
                >
                  {checkPosition === 'left' ? (
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === option.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  ) : null}
                  {option.icon ? (
                    <span className="mr-2 flex size-4 shrink-0 items-center justify-center">
                      {option.icon}
                    </span>
                  ) : null}
                  <span className="min-w-0 flex-1 truncate">
                    {option.label}
                  </span>
                  {checkPosition === 'right' ? (
                    <Check
                      className={cn(
                        'ml-2 h-4 w-4 shrink-0',
                        value === option.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  ) : null}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
